import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Plus, MessageSquare, Clock, AlertCircle, CheckCircle, 
  Send, ChevronRight, Filter, Search, Loader2, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'technical', label: 'Technical Issue' },
  { value: 'account_access', label: 'Account / Access' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'general', label: 'General Question' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

const STATUSES = [
  { value: 'open', label: 'Open', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Clock },
  { value: 'waiting_for_admin', label: 'Waiting for You', color: 'bg-purple-100 text-purple-700', icon: MessageSquare },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-700', icon: CheckCircle },
];

export default function SupportTickets() {
  const { api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'general',
    description: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchTickets();
  }, [filterStatus]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await api.get(`/tickets?${params}`);
      setTickets(response.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      toast.error('Please fill in subject and description');
      return;
    }

    try {
      setSending(true);
      await api.post('/tickets', newTicket);
      toast.success('Ticket created successfully');
      setShowCreateDialog(false);
      setNewTicket({ subject: '', category: 'general', description: '', priority: 'medium' });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;

    try {
      setSending(true);
      const response = await api.post(`/tickets/${selectedTicket.id}/reply`, {
        message: replyText
      });
      setSelectedTicket(response.data);
      setReplyText('');
      toast.success('Reply sent');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleReopenTicket = async () => {
    try {
      const response = await api.put(`/tickets/${selectedTicket.id}/status`, {
        status: 'open'
      });
      setSelectedTicket(response.data);
      toast.success('Ticket reopened');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to reopen ticket');
    }
  };

  const openTicketDetail = async (ticket) => {
    try {
      const response = await api.get(`/tickets/${ticket.id}`);
      setSelectedTicket(response.data);
      setShowTicketDialog(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const getStatusInfo = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];
  const getPriorityInfo = (priority) => PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
  const getCategoryLabel = (category) => CATEGORIES.find(c => c.value === category)?.label || category;

  const filteredTickets = tickets.filter(ticket =>
    ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.ticket_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Support Tickets</h1>
          <p className="text-slate-500 mt-1">Get help from our support team</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-ticket-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:border-yellow-300" onClick={() => setFilterStatus('open')}>
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{openCount}</p>
            <p className="text-sm text-slate-500">Open</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => setFilterStatus('in_progress')}>
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{inProgressCount}</p>
            <p className="text-sm text-slate-500">In Progress</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-green-300" onClick={() => setFilterStatus('resolved')}>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold">{resolvedCount}</p>
            <p className="text-sm text-slate-500">Resolved</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search tickets..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tickets</SelectItem>
            {STATUSES.map(status => (
              <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Ticket List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No tickets found</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                Create Your First Ticket
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {filteredTickets.map(ticket => {
                const statusInfo = getStatusInfo(ticket.status);
                const priorityInfo = getPriorityInfo(ticket.priority);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <div
                    key={ticket.id}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => openTicketDetail(ticket)}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-slate-400">{ticket.ticket_number}</span>
                          <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
                          <Badge className={statusInfo.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                        </div>
                        <h3 className="font-medium text-slate-900 truncate">{ticket.subject}</h3>
                        <p className="text-sm text-slate-500 mt-1">
                          {getCategoryLabel(ticket.category)} • {new Date(ticket.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>
              Describe your issue and we'll get back to you as soon as possible
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Subject *</Label>
              <Input
                placeholder="Brief description of your issue"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                data-testid="ticket-subject-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={newTicket.category} 
                  onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select 
                  value={newTicket.priority} 
                  onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                placeholder="Please describe your issue in detail..."
                rows={5}
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                data-testid="ticket-description-input"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTicket} disabled={sending} data-testid="submit-ticket-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Ticket
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-slate-400">{selectedTicket.ticket_number}</span>
                  <Badge className={getPriorityInfo(selectedTicket.priority).color}>
                    {getPriorityInfo(selectedTicket.priority).label}
                  </Badge>
                  <Badge className={getStatusInfo(selectedTicket.status).color}>
                    {getStatusInfo(selectedTicket.status).label}
                  </Badge>
                </div>
                <DialogTitle>{selectedTicket.subject}</DialogTitle>
                <DialogDescription>
                  {getCategoryLabel(selectedTicket.category)} • Created {new Date(selectedTicket.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto py-4 space-y-4 min-h-[300px]">
                {selectedTicket.messages?.map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`flex ${msg.sender_role === 'super_admin' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      msg.sender_role === 'super_admin' 
                        ? 'bg-slate-100 text-slate-900' 
                        : 'bg-orange-500 text-white'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">
                          {msg.sender_role === 'super_admin' ? 'Support Team' : 'You'}
                        </span>
                        <span className="text-xs opacity-70">
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4">
                  {selectedTicket.status === 'resolved' ? (
                    <div className="text-center">
                      <p className="text-sm text-slate-500 mb-2">This ticket has been resolved</p>
                      <Button variant="outline" onClick={handleReopenTicket}>
                        Reopen Ticket
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your reply..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={2}
                        className="flex-1"
                      />
                      <Button onClick={handleSendReply} disabled={sending || !replyText.trim()}>
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
